import { createConfigParametersRepository } from '../configParametersRepository';
import { createCustomDimensionsRepository } from './customDimensionsRepository';
import { getGaCustomDimensions } from './customDimensionsRepository/standalone';
import { createECommerceTrackingClient } from './eCommerceTrackingClient';
import { createFirebaseAdapter } from './firebase/firebaseAdapter';
import { CustomDimensionsOrParams } from './google-analytics/ga4Types';
import {
  createDatalayerUpdater,
  fireDataLayerEvent,
  isTMSVariableGlobal,
  loadContainer,
  syncTcVars,
  trackTMSPage,
  updateAndSyncDatalayer,
} from './tag-commander/datalayer';
import { datalayerStorageClient } from './tag-commander/datalayerStorage';
import { createDatalayerTrackingClient } from './tag-commander/datalayerTrackingClient';
import {
  Datalayer,
  TMSFetchOptions,
  TrackTMSPageDatalayer,
} from './tag-commander/types';
import { flatten } from './tag-commander/utils';
import { createThirdPartyTrackingAgent } from './thirdPartyTrackingAgent';
import { trackPage as trackGAPage } from './thirdPartyTrackingAgent/pages';
import { createTrackingParamsRepository } from './trackingParamsRepository';
import { getMktportalParams } from './trackingParamsRepository/mktportal';
import { getUtmParameters } from './trackingParamsRepository/utm';
import { identifyAnalyticsClient } from './utils/analytics-identification';
import { shouldSkipTracking } from './utils/cookie';
import { getCustomDimensionsFromParams } from './google-analytics/ga4';

type SyncTrackingDataInput = {
  gaCustomDimensions: Partial<CustomDimensionsOrParams>;
  tcDatalayer: Partial<Datalayer>;
};

/**
 * Syncronize custom dimensions/params and data layer from OF1.
 * @param {object} input - The parameters to sync the custom dimensions/params and data layer.
 * @param {object} input.gaCustomDimensions - The custom dimensions/params to sync.
 * @param {object} input.tcDatalayer - The data layer to sync.
 */
const syncTrackingData = async ({
  gaCustomDimensions,
  tcDatalayer,
}: SyncTrackingDataInput) => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  storageClient?.updateCustomParamsAndDimensions(gaCustomDimensions);
  updateAndSyncDatalayer(tcDatalayer);
};

export type { Datalayer, TMSFetchOptions, TrackTMSPageDatalayer };

export const thirdPartyTracking = {
  getCustomDimensionsFromParams,
  createCustomDimensionsRepository, // WARNING: use only in frontend-shell, frontend-html5 & frontend-home
  createECommerceTrackingClient, // WARNING: use only in frontend-shell, frontend-html5 & frontend-home
  createDatalayerTrackingClient, // WARNING: use only in frontend-shell, frontend-html5 & frontend-home
  createFirebaseAdapter, // WARNING: use only in frontend-shell, frontend-html5 & frontend-home
  syncTrackingData,
  createDatalayerUpdater, // TODO: this function is deprecated. In product library, use a custom hook from frontend-core
  getDatalayer: datalayerStorageClient().getDatalayerGlobalParams,
  fireDataLayerEvent,
  isTMSVariableGlobal,
  getUtmParams: getUtmParameters,
  identifyAnalyticsClient,
  createThirdPartyTrackingAgent,
  createTrackingParamsRepository,
  getUtmParameters, // WARNING: use only in frontend-core, frontend-html5 & frontend-home
  getMktportalParams, // WARNING: use only in frontend-core, frontend-html5 & frontend-home
  getGaCustomParameters: getGaCustomDimensions, // WARNING: use only in frontend-core, frontend-html5 & frontend-home
  trackGAPage, // WARNING: use only in frontend-core, frontend-html5 & frontend-home
  trackTMSPage, // WARNING: use only in frontend-core, frontend-html5 & frontend-home
  syncTcVars,
  loadContainer,
  flatten,
  shouldSkipTracking,
};

export type { ThirdPartyTrackingAgentArgs } from './thirdPartyTrackingAgent/types';
